'use client'

import React, { memo, useCallback, useEffect, useMemo } from 'react'

import { twMerge } from 'tailwind-merge'

import Drawer from '@/components/common/Drawer'
import { CuiSafeOrganizationBlock } from '@/components/organizations/Cui'

import { useActiveOrgHasCuiEnabled } from '@/hooks/organization/useActiveOrg'
import {
  useIdentifierLookup,
  useInfiniteRecipientSearch,
  useRecipientCount,
} from '@/hooks/recipient-search'

import { detectIdentifierPattern } from '@/lib/recipient-search/identifierDetection'

import { useOrganizationStore } from '@/stores/OrganizationStore'
import { useRecipientSearchStore } from '@/stores/RecipientSearchStore'

import { RecipientFilterProvider } from './RecipientFilterContext'
import RecipientFilters from './RecipientFilters'
import RecipientResults from './RecipientResults'
import RecipientToolbar from './RecipientToolbar'

interface RecipientSearchProps {
  className?: string
}

/**
 * Main Recipient Search component
 *
 * Provides a full-featured search interface for federal award recipients
 * including text search, filters, sorting, and infinite scroll pagination.
 */
const RecipientSearch: React.FC<RecipientSearchProps> = ({ className }) => {
  const activeOrgId = useOrganizationStore((s) => s.activeOrgId)
  const { data: isCuiEnabled, isLoading: isCuiLoading } =
    useActiveOrgHasCuiEnabled()

  if (!activeOrgId || isCuiLoading) {
    return null
  }

  if (isCuiEnabled) {
    return (
      <CuiSafeOrganizationBlock
        orgId={activeOrgId}
        featureName="Recipient Search"
        redirectTo="/recipients/search"
      />
    )
  }

  return <RecipientSearchInner className={className} />
}

const RecipientSearchInner: React.FC<RecipientSearchProps> = ({
  className,
}) => {
  const {
    query,
    filters,
    filterPanelOpen,
    setFilterPanelOpen,
    setParentRecipient,
    setIsSearching,
    addNaicsCode,
    toggleCertification,
    addState,
  } = useRecipientSearchStore()

  // Auto-detect identifier pattern from the debounced query
  const detectedPatternType = detectIdentifierPattern(query)

  // Auto-lookup: fire exact-match search when pattern detected and not in strict ID mode
  const { data: pinnedResult } = useIdentifierLookup(
    filters.identifier_type ? null : detectedPatternType,
    query,
  )

  const hasExactMatch = pinnedResult != null

  // Fetch recipients with infinite scroll (cursor-based pagination)
  const {
    data,
    isLoading,
    isFetching,
    isFetchingNextPage,
    hasNextPage,
    fetchNextPage,
  } = useInfiniteRecipientSearch(filters, {
    enabled: true,
  })

  // Fetch total count separately (cursor pagination doesn't include total)
  // Count query uses placeholderData to preserve previous count during transitions
  const { data: countData } = useRecipientCount(filters, {
    enabled: true,
  })
  const total = countData?.count ?? 0
  // Count is "known" when we have actual data (not defaulting to 0)
  const isCountKnown = countData !== undefined

  // Update searching state
  useEffect(() => {
    setIsSearching(isFetching)
  }, [isFetching, setIsSearching])

  // Flatten paginated results
  const results = useMemo(() => {
    if (!data?.pages) return []
    return data.pages.flatMap((page) => page.results)
  }, [data?.pages])

  // Handle load more
  const handleLoadMore = useCallback(() => {
    if (hasNextPage && !isFetchingNextPage) {
      fetchNextPage()
    }
  }, [hasNextPage, isFetchingNextPage, fetchNextPage])

  // Memoize the set of selected NAICS codes for highlight matching
  const selectedNaicsCodes = useMemo(
    () => new Set(filters.naics_codes),
    [filters.naics_codes],
  )

  // Close mobile filter drawer
  const handleCloseFilterDrawer = useCallback(() => {
    setFilterPanelOpen(false)
  }, [setFilterPanelOpen])

  return (
    <RecipientFilterProvider
      onFilterByNaics={addNaicsCode}
      onFilterByCertification={toggleCertification}
      onFilterByState={addState}
      onShowSubsidiaries={setParentRecipient}
      selectedNaicsCodes={selectedNaicsCodes}
    >
      <div
        className={twMerge(
          'flex w-full h-full bg-white dark:bg-primary-900',
          className,
        )}
      >
        {/* Desktop Filter Panel */}
        <div className="hidden lg:flex flex-col overflow-y-auto w-[280px] xl:w-[320px] border-r border-primary-200 dark:border-primary-700 bg-white dark:bg-primary-900">
          <RecipientFilters />
        </div>

        {/* Mobile Filter Drawer */}
        <div className="lg:hidden">
          <Drawer open={filterPanelOpen} toggleDrawer={handleCloseFilterDrawer}>
            <RecipientFilters />
          </Drawer>
        </div>

        {/* Main Content */}
        <div className="flex flex-col flex-1 min-w-0">
          {/* Toolbar */}
          <RecipientToolbar
            totalCount={total}
            isLoading={isLoading}
            hasExactMatch={hasExactMatch}
          />

          {/* Results */}
          <div className="flex-1 overflow-hidden">
            <RecipientResults
              results={results}
              total={total}
              isCountKnown={isCountKnown}
              isLoading={isLoading}
              isRefetching={
                isFetching &&
                !isLoading &&
                !isFetchingNextPage &&
                results.length > 0
              }
              isFetchingNextPage={isFetchingNextPage}
              hasNextPage={hasNextPage}
              onLoadMore={handleLoadMore}
              pinnedResult={pinnedResult}
            />
          </div>
        </div>
      </div>
    </RecipientFilterProvider>
  )
}

export default memo(RecipientSearch)
